import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { fromEvent } from 'rxjs';
import { StoriesService } from '../stories/stories.service';

/** Min time in background before triggering a foreground sync. */
const SYNC_THRESHOLD_MS = 60_000;
/** Guard against duplicate sync when both visibility and appStateChange fire. */
const SYNC_COOLDOWN_MS = 5_000;

/**
 * App foreground/background lifecycle and stale sync after a long pause.
 *
 * Browser/PWA: Page Visibility API. Native: Capacitor App appStateChange.
 */
@Injectable({ providedIn: 'root' })
export class AppForegroundService {
  private readonly stories = inject(StoriesService);

  private hiddenAt: number | null = null;
  private lastSyncAt = 0;
  private lastForegroundAt = 0;
  private activeChatId: number | null = null;
  private chatSyncHandler: (() => void) | null = null;
  private chatForegroundHandler: (() => void) | null = null;

  constructor() {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          if (document.visibilityState === 'hidden') {
            this.onBecomeHidden();
          } else if (document.visibilityState === 'visible') {
            this.onBecomeVisible();
          }
        });
    }

    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          this.onBecomeHidden();
        } else {
          this.onBecomeVisible();
        }
      });
    }
  }

  /** Chat page open: track its sync and UI recovery handlers while foregrounded. */
  registerChat(
    chatId: number,
    syncFn: () => void,
    foregroundFn?: () => void,
  ): void {
    this.activeChatId = chatId;
    this.chatSyncHandler = syncFn;
    this.chatForegroundHandler = foregroundFn ?? null;
  }

  unregisterChat(): void {
    this.activeChatId = null;
    this.chatSyncHandler = null;
    this.chatForegroundHandler = null;
  }

  private onBecomeHidden(): void {
    this.hiddenAt = Date.now();
  }

  private onBecomeVisible(): void {
    const wasBackgrounded = this.hiddenAt !== null;
    const hiddenDuration = this.hiddenAt ? Date.now() - this.hiddenAt : 0;
    this.hiddenAt = null;

    // Capacitor and Page Visibility can both emit the resume event.  Recover
    // persistent WebView widgets once, even after a short background pause.
    if (
      wasBackgrounded &&
      Date.now() - this.lastForegroundAt >= SYNC_COOLDOWN_MS
    ) {
      this.lastForegroundAt = Date.now();
      this.chatForegroundHandler?.();
    }

    if (hiddenDuration < SYNC_THRESHOLD_MS) {
      return;
    }

    if (Date.now() - this.lastSyncAt < SYNC_COOLDOWN_MS) {
      return;
    }
    this.lastSyncAt = Date.now();

    void this.stories.getStories();

    this.chatSyncHandler?.();
  }
}
